import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Ban, Paperclip, Plus } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Cajon } from "@/componentes/ui/Cajon"
import { EstadoCarga, EstadoError, EstadoVacio } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { type ArchivoParaVer, VistaPreviaArchivo } from "@/componentes/ui/VistaPreviaArchivo"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

import { ModalRegistro } from "./ModalRegistro"
import {
  etiquetaPeriodicidad,
  type FilaTablero,
  fechaCorta,
  type Registro,
  SEMAFORO,
} from "./tipos"

/**
 * Una obligación: qué es, su estado y TODO lo que se registró, también lo anulado
 * —tachado y con su motivo—, porque es lo que explica por qué cambió el semáforo.
 */
export function FichaObligacion({ fila, onCerrar }: { fila: FilaTablero; onCerrar: () => void }) {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeRegistrar = tienePermiso(usuario, "cumplimiento:registrar")
  const { obligacion: ob, ente } = fila
  const [registrando, setRegistrando] = useState(false)
  const [anulando, setAnulando] = useState<Registro | null>(null)
  const [motivo, setMotivo] = useState("")
  const [soporte, setSoporte] = useState<ArchivoParaVer | null>(null)

  const registros = useQuery({
    queryKey: ["cumplimiento", "registros", ob.id],
    queryFn: () => api.get<Registro[]>(`/api/v1/cumplimiento/obligaciones/${ob.id}/registros`),
  })
  const anular = useMutation({
    mutationFn: (r: Registro) =>
      api.post<Registro>(`/api/v1/cumplimiento/registros/${r.id}/anular`, { motivo }),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["cumplimiento"] })
      setAnulando(null)
      setMotivo("")
    },
    meta: { exito: "Registro anulado" },
  })

  return (
    <Cajon abierto={true} titulo={`${ente.codigo} · ${ob.nombre}`} onCerrar={onCerrar}>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Insignia tono={SEMAFORO[fila.estado].tono}>{SEMAFORO[fila.estado].etiqueta}</Insignia>
        {fila.vence && (
          <span className="text-sm tabular-nums">Vence el {fechaCorta(fila.vence)}</span>
        )}
        {puedeRegistrar && ob.activa && (
          <Boton variante="primario" className="ml-auto" onClick={() => setRegistrando(true)}>
            <Plus size={15} aria-hidden="true" />
            Registrar
          </Boton>
        )}
      </div>

      <dl className="mb-5 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <div>
          <dt style={{ color: "var(--fg-muted)" }}>Ente</dt>
          <dd>{ente.nombre}</dd>
        </div>
        <div>
          <dt style={{ color: "var(--fg-muted)" }}>Periodicidad</dt>
          <dd>
            {etiquetaPeriodicidad(ob.periodicidad)}
            {ob.dia_limite ? ` · día ${ob.dia_limite}` : ""}
          </dd>
        </div>
        {ob.base_legal && (
          <div>
            <dt style={{ color: "var(--fg-muted)" }}>Base legal</dt>
            <dd>{ob.base_legal}</dd>
          </div>
        )}
        {fila.responsable && (
          <div>
            <dt style={{ color: "var(--fg-muted)" }}>Responsable</dt>
            <dd>{fila.responsable}</dd>
          </div>
        )}
        {ob.descripcion && (
          <div className="sm:col-span-2">
            <dt style={{ color: "var(--fg-muted)" }}>Nota</dt>
            <dd>{ob.descripcion}</dd>
          </div>
        )}
      </dl>

      <h3 className="mb-2 text-sm font-semibold">Registros</h3>
      {registros.isPending ? (
        <EstadoCarga />
      ) : registros.isError ? (
        <EstadoError
          titulo="No se pudieron leer los registros."
          error={registros.error}
          onReintentar={() => registros.refetch()}
        />
      ) : registros.data.length === 0 ? (
        <EstadoVacio
          titulo="Todavía no hay nada registrado"
          texto="Registra la última presentación o solvencia con su soporte para que el semáforo diga la verdad."
        />
      ) : (
        <ul className="grid gap-2">
          {registros.data.map((r) => (
            <li
              key={r.id}
              className="tarjeta p-3 text-sm"
              style={{ opacity: r.anulado_en ? 0.6 : undefined }}
            >
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className={r.anulado_en ? "font-medium line-through" : "font-medium"}>
                  {r.periodo}
                </span>
                <span className="tabular-nums" style={{ color: "var(--fg-muted)" }}>
                  {fechaCorta(r.fecha)}
                  {r.vence ? ` → vence ${fechaCorta(r.vence)}` : ""}
                </span>
                {r.numero && <span className="tabular-nums">N° {r.numero}</span>}
                <span className="ml-auto flex gap-1">
                  {r.tiene_soporte && (
                    <Boton
                      variante="fantasma"
                      tamano="sm"
                      aria-label={`Ver el soporte de ${r.periodo}`}
                      onClick={() =>
                        setSoporte({
                          ruta: `/api/v1/cumplimiento/registros/${r.id}/soporte`,
                          nombre: r.soporte_nombre ?? `${ob.codigo} ${r.periodo}`,
                        })
                      }
                    >
                      <Paperclip size={15} aria-hidden="true" />
                    </Boton>
                  )}
                  {puedeRegistrar && !r.anulado_en && (
                    <Boton
                      variante="fantasma"
                      tamano="sm"
                      aria-label={`Anular el registro de ${r.periodo}`}
                      onClick={() => setAnulando(r)}
                    >
                      <Ban size={15} aria-hidden="true" />
                    </Boton>
                  )}
                </span>
              </div>
              {r.observacion && <p className="mt-1">{r.observacion}</p>}
              {r.anulado_en && (
                <p className="mt-1" style={{ color: "var(--destructive)" }}>
                  Anulado: {r.motivo_anulacion}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {registrando && <ModalRegistro obligacion={ob} onCerrar={() => setRegistrando(false)} />}
      <Modal
        abierto={anulando !== null}
        titulo="Anular el registro"
        descripcion="Queda en el historial, tachado y con su motivo."
        onCerrar={() => setAnulando(null)}
      >
        <label htmlFor="cump-motivo" className="mb-1 block text-xs font-medium uppercase">
          Motivo (al menos 10 caracteres)
        </label>
        <textarea
          id="cump-motivo"
          rows={3}
          className="campo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
        />
        {anular.error && (
          <p role="alert" className="mt-2 text-sm" style={{ color: "var(--destructive)" }}>
            {anular.error instanceof ErrorApi ? anular.error.message : "No se pudo anular"}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Boton variante="fantasma" onClick={() => setAnulando(null)}>
            Cancelar
          </Boton>
          <Boton
            variante="peligro"
            disabled={motivo.trim().length < 10 || anular.isPending}
            onClick={() => anulando && anular.mutate(anulando)}
          >
            Anular
          </Boton>
        </div>
      </Modal>
      <VistaPreviaArchivo archivo={soporte} onCerrar={() => setSoporte(null)} />
    </Cajon>
  )
}
